import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  ClockIcon,
  WarningIcon,
  WifiHighIcon,
  WifiSlashIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

interface SystemStatusProps {
  className?: string;
}

type SystemHealthStatus = 'healthy' | 'warning' | 'error';

interface SystemCheck {
  id: string;
  name: string;
  status: SystemHealthStatus | 'loading';
  message: string;
  lastChecked: Date;
  details?: string;
}

export function SystemStatus({ className }: Readonly<SystemStatusProps>) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [systemChecks, setSystemChecks] = useState<SystemCheck[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Perform system checks
  useEffect(() => {
    const performSystemChecks = () => {
      const checks: SystemCheck[] = [
        {
          id: 'storage',
          name: 'Local Storage',
          status: checkLocalStorage() ? 'healthy' : 'error',
          message: checkLocalStorage() ? 'Available' : 'Not available',
          lastChecked: new Date(),
          details: checkLocalStorage()
            ? 'Data persistence is working correctly'
            : 'Cannot save your progress',
        },
        {
          id: 'memory',
          name: 'Memory Usage',
          status: checkMemoryUsage(),
          message: getMemoryMessage(),
          lastChecked: new Date(),
          details: getMemoryDetails(),
        },
        {
          id: 'performance',
          name: 'Performance',
          status: checkPerformance(),
          message: getPerformanceMessage(),
          lastChecked: new Date(),
          details: getPerformanceDetails(),
        },
      ];

      setSystemChecks(checks);
      setLastUpdate(new Date());
    };

    performSystemChecks();
    const interval = setInterval(performSystemChecks, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const checkLocalStorage = (): boolean => {
    try {
      const test = 'test';

      localStorage.setItem(test, test);
      localStorage.removeItem(test);

      return true;
    } catch {
      return false;
    }
  };

  const checkMemoryUsage = (): SystemHealthStatus => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

      if (usageRatio > 0.9) {
        return 'error';
      }
      if (usageRatio > 0.7) {
        return 'warning';
      }

      return 'healthy';
    }

    return 'healthy'; // Default to healthy if memory API not available
  };

  const getMemoryMessage = (): string => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usedMB = Math.round(memory.usedJSHeapSize / 1024 / 1024);
      const totalMB = Math.round(memory.jsHeapSizeLimit / 1024 / 1024);

      return `${usedMB}MB / ${totalMB}MB`;
    }

    return 'Unknown';
  };

  const getMemoryDetails = (): string => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;

      if (usageRatio > 0.9) {
        return 'Memory usage is very high. Consider refreshing the page.';
      }
      if (usageRatio > 0.7) {
        return 'Memory usage is elevated. Monitor for performance issues.';
      }

      return 'Memory usage is normal.';
    }

    return 'Memory monitoring not available in this browser.';
  };

  const checkPerformance = (): SystemHealthStatus => {
    const connectionType = (navigator as any).connection?.effectiveType;

    if (connectionType === 'slow-2g' || connectionType === '2g') {
      return 'error';
    }
    if (connectionType === '3g') {
      return 'warning';
    }

    return 'healthy';
  };

  const getPerformanceMessage = (): string => {
    const connectionType = (navigator as any).connection?.effectiveType;

    switch (connectionType) {
      case 'slow-2g':
      case '2g':
        return 'Slow connection';
      case '3g':
        return 'Moderate connection';
      case '4g':
        return 'Fast connection';
      default:
        return 'Connection speed unknown';
    }
  };

  const getPerformanceDetails = (): string => {
    const connectionType = (navigator as any).connection?.effectiveType;

    switch (connectionType) {
      case 'slow-2g':
      case '2g':
        return 'Very slow internet connection detected. Some features may load slowly.';
      case '3g':
        return 'Moderate internet connection. Most features should work normally.';
      case '4g':
        return 'Fast internet connection. All features should work optimally.';
      default:
        return 'Connection speed monitoring not available.';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return (
          <CheckCircleIcon className='w-4 h-4 text-green-600' weight='fill' />
        );
      case 'warning':
        return (
          <WarningIcon className='w-4 h-4 text-yellow-600' weight='fill' />
        );
      case 'error':
        return <XCircleIcon className='w-4 h-4 text-red-600' weight='fill' />;
      case 'loading':
        return <ClockIcon className='w-4 h-4 text-blue-600' />;
      default:
        return <ClockIcon className='w-4 h-4 text-gray-600' />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      case 'loading':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getOverallHealth = (checks: SystemCheck[]): SystemHealthStatus => {
    if (checks.length === 0) {
      return 'healthy';
    }
    if (checks.every(check => check.status === 'healthy')) {
      return 'healthy';
    }
    if (checks.some(check => check.status === 'error')) {
      return 'error';
    }

    return 'warning';
  };

  const getStatusMessage = (
    health: 'healthy' | 'warning' | 'error'
  ): string => {
    if (health === 'healthy') {
      return 'All systems operational';
    }
    if (health === 'warning') {
      return 'Some issues detected';
    }

    return 'System issues detected';
  };

  const overallHealth = getOverallHealth(systemChecks);

  const healthScore =
    systemChecks.length > 0
      ? (systemChecks.filter(check => check.status === 'healthy').length /
          systemChecks.length) *
        100
      : 100;

  if (!isOnline) {
    return (
      <Alert className={className} variant='destructive'>
        <WifiSlashIcon className='w-4 h-4' />
        <AlertTitle>You're offline</AlertTitle>
        <AlertDescription>
          Some features may not work properly. Check your internet connection.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className={className}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className='cursor-pointer hover:bg-accent/50 transition-colors'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {isOnline ? (
                  <WifiHighIcon className='w-5 h-5 text-green-600' />
                ) : (
                  <WifiSlashIcon className='w-5 h-5 text-red-600' />
                )}
                <div>
                  <CardTitle className='text-sm'>System Status</CardTitle>
                  <div className='flex items-center gap-2 mt-1'>
                    {getStatusIcon(overallHealth)}
                    <span
                      className={`text-xs ${getStatusColor(overallHealth)}`}
                    >
                      {getStatusMessage(overallHealth)}
                    </span>
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-2'>
                <Badge variant='outline' className='text-xs'>
                  {Math.round(healthScore)}%
                </Badge>
                {isExpanded ? (
                  <CaretUpIcon className='w-4 h-4 text-muted-foreground' />
                ) : (
                  <CaretDownIcon className='w-4 h-4 text-muted-foreground' />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className='pt-0'>
            <div className='space-y-3'>
              <div className='flex items-center justify-between mb-2'>
                <Progress value={healthScore} className='flex-1 mr-3' />
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => window.location.reload()}
                  className='gap-1'
                >
                  <ArrowClockwiseIcon className='w-3 h-3' />
                  Refresh
                </Button>
              </div>

              {systemChecks.map(check => (
                <div
                  key={check.id}
                  className='flex items-start gap-3 p-2 rounded border'
                >
                  {getStatusIcon(check.status)}
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center justify-between mb-1'>
                      <span className='text-sm font-medium'>{check.name}</span>
                      <span
                        className={`text-xs ${getStatusColor(check.status)}`}
                      >
                        {check.message}
                      </span>
                    </div>
                    {check.details && (
                      <p className='text-xs text-muted-foreground'>
                        {check.details}
                      </p>
                    )}
                    <p className='text-xs text-muted-foreground'>
                      Last checked: {check.lastChecked.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}

              <div className='text-xs text-muted-foreground text-center pt-2 border-t'>
                Last updated: {lastUpdate.toLocaleTimeString()}
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
